import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';
import { UserId, Uuid } from '../Base';
import Repository, { RepositoryElement, RepositoryJson } from '../Repository';

type SubscriptionMonthlyTickets = {
  monthly: number;
};

type SubscriptionPermanentTickets = {
  permanent: number;
};

export type SpaceMembershipTierInventoryJson<T = number> = {
  readonly tierId: Uuid; //identifier
  readonly classroomId: Uuid; //identifier
  readonly userId: UserId; // identifier
  createdAt: T;
  lastModifiedAt: T;
  subscriptionTickets:
    | SubscriptionMonthlyTickets
    | SubscriptionPermanentTickets;
};

export class SpaceMembershipTierInventory
  extends RepositoryElement
  implements SpaceMembershipTierInventoryJson<Date>
{
  readonly tierId!: Uuid; //identifier
  readonly classroomId!: Uuid; //identifier
  readonly userId!: UserId; // identifier
  createdAt!: Date;
  lastModifiedAt!: Date;
  subscriptionTickets!:
    | SubscriptionMonthlyTickets
    | SubscriptionPermanentTickets;

  constructor(inventory: SpaceMembershipTierInventoryJson<Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(inventory));
  }

  id(): string {
    return `${this.classroomId}_${this.userId}_${this.tierId}`;
  }

  toJSON(): SpaceMembershipTierInventoryJson {
    return deepTransformDate(
      this,
    ) as unknown as SpaceMembershipTierInventoryJson;
  }

  get tickets() {
    if ('monthly' in this.subscriptionTickets) {
      return this.subscriptionTickets.monthly;
    } else {
      return this.subscriptionTickets.permanent;
    }
  }

  get isPermanent() {
    return 'permanent' in this.subscriptionTickets;
  }

  get subscriptionType() {
    if ('monthly' in this.subscriptionTickets) {
      return 'monthly' as const;
    } else {
      return 'permanent' as const;
    }
  }
}

export enum SpaceMembershipTierInventoryMapType {
  ActiveUser = 'activeUser',
}
export type SpaceMembershipTierInventoryMapField =
  SpaceMembershipTierInventoryMapType;
export enum SpaceMembershipTierInventoryListType {
  All = 'all',
}
export type SpaceMembershipTierInventoryListField = string; // classroomId

export class SpaceMembershipTierInventoryRepository extends Repository<
  SpaceMembershipTierInventory,
  SpaceMembershipTierInventoryJson<Date | number>,
  SpaceMembershipTierInventoryMapField,
  SpaceMembershipTierInventoryListField
> {
  constructor(
    repository?: RepositoryJson<
      SpaceMembershipTierInventoryJson<Date | number>,
      SpaceMembershipTierInventoryMapField,
      SpaceMembershipTierInventoryListField
    >,
  ) {
    super(
      {
        ...repository,
        mapFields: Repository.initFields(repository?.mapFields, {
          [SpaceMembershipTierInventoryMapType.ActiveUser]: {},
        }),
      },
      (x) => new SpaceMembershipTierInventory(x),
    );
  }
}
